import React, { useEffect, useState } from 'react'
import {
    Box,
    Button,
    Chip,
    Container,
    Grid,
    Skeleton,
    Typography,
} from '@mui/material'
import { Add } from '@mui/icons-material'
import { Helmet } from 'react-helmet'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { api } from '../../../services/Main/Api'
import { getCondoId } from '../../../services/Main/AuthStorage'
import NoResultsAnimation from '../../../components/Shared/animations/NoResultsAnimation'
import AccountCard from './components/AccountCard'
import BankAccountFormModal from './BankAccountFormModal'
import ImportStatementModal from './ImportStatementModal'

const BankAccountList = () => {
    const navigate = useNavigate()
    const { enqueueSnackbar } = useSnackbar()

    const [accounts, setAccounts] = useState(null)
    const [loading, setLoading] = useState(true)
    const [modalOpen, setModalOpen] = useState(false)
    const [editData, setEditData] = useState(null)
    const [importModalOpen, setImportModalOpen] = useState(false)
    const [importAccountId, setImportAccountId] = useState(null)
    const [filterActive, setFilterActive] = useState(true)

    const fetchAccounts = () => {
        setLoading(true)
        const params = { condo_id: getCondoId() }
        if (filterActive !== null) params.is_active = filterActive

        api.get('cashflow/managed-bank-accounts/', { params })
            .then((res) => {
                setAccounts(res.data?.results || res.data || [])
            })
            .catch(() => {
                enqueueSnackbar('Erro ao carregar contas', { variant: 'error' })
                setAccounts([])
            })
            .finally(() => setLoading(false))
    }

    useEffect(() => {
        window.scrollTo(0, 0)
        fetchAccounts()
    }, [filterActive])

    const handleViewEntries = (accountId) => {
        navigate(`/financeiro/contas-bancarias/${accountId}/extrato`)
    }

    const handleEdit = (account) => {
        setEditData(account)
        setModalOpen(true)
    }

    const handleImport = (accountId) => {
        setImportAccountId(accountId)
        setImportModalOpen(true)
    }

    const handleCreate = () => {
        setEditData(null)
        setModalOpen(true)
    }

    return (
        <>
            <Helmet>
                <title>Contas Bancárias - Bloco B3</title>
            </Helmet>

            <Box sx={{ bgcolor: '#F8FAFC', minHeight: '100vh' }}>
                <Container maxWidth="xl" sx={{ py: 3 }}>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={3}
                        flexWrap="wrap"
                        gap={2}
                    >
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1E293B' }}>
                                Contas Bancárias
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#64748B', mt: 0.5 }}>
                                Gerencie suas contas bancárias externas
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={handleCreate}
                            sx={{
                                bgcolor: '#3B82F6',
                                '&:hover': { bgcolor: '#2563EB' },
                                textTransform: 'none',
                                fontWeight: 600,
                                borderRadius: 2,
                                px: 3,
                            }}
                        >
                            Nova Conta
                        </Button>
                    </Box>

                    <Box display="flex" gap={1} mb={3}>
                        {[
                            { key: true, label: 'Ativas' },
                            { key: false, label: 'Inativas' },
                            { key: null, label: 'Todas' },
                        ].map((opt) => (
                            <Chip
                                key={String(opt.key)}
                                label={opt.label}
                                onClick={() => setFilterActive(opt.key)}
                                sx={{
                                    bgcolor:
                                        filterActive === opt.key ? '#3B82F614' : 'transparent',
                                    color:
                                        filterActive === opt.key ? '#3B82F6' : '#64748B',
                                    border:
                                        filterActive === opt.key
                                            ? '1.5px solid #3B82F6'
                                            : '1.5px solid #E2E8F0',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: '#F1F5F9' },
                                }}
                            />
                        ))}
                    </Box>

                    {loading || accounts === null ? (
                        <Grid container spacing={2.5}>
                            {[1, 2, 3].map((i) => (
                                <Grid item xs={12} sm={6} md={4} key={i}>
                                    <Skeleton variant="rounded" height={200} />
                                </Grid>
                            ))}
                        </Grid>
                    ) : accounts.length === 0 ? (
                        <Box sx={{ py: 8 }}>
                            <NoResultsAnimation />
                            <Typography
                                variant="body1"
                                sx={{ color: '#94A3B8', textAlign: 'center', mt: 2 }}
                            >
                                Nenhuma conta bancária cadastrada.
                            </Typography>
                        </Box>
                    ) : (
                        <Grid container spacing={2.5}>
                            {accounts.map((account) => (
                                <Grid item xs={12} sm={6} md={4} key={account.id}>
                                    <AccountCard
                                        account={account}
                                        onEdit={handleEdit}
                                        onViewEntries={handleViewEntries}
                                        onImport={handleImport}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Container>
            </Box>

            <BankAccountFormModal
                open={modalOpen}
                onClose={() => {
                    setModalOpen(false)
                    setEditData(null)
                }}
                editData={editData}
                onSave={fetchAccounts}
            />

            <ImportStatementModal
                open={importModalOpen}
                onClose={() => {
                    setImportModalOpen(false)
                    setImportAccountId(null)
                }}
                accountId={importAccountId}
                onSuccess={fetchAccounts}
            />
        </>
    )
}

export default BankAccountList
